import React, { useState } from "react";
import api from "../lib/adminApi";
import { UserCog, Copy, Check, X, Loader2, AlertTriangle } from "lucide-react";

const TTL_OPTIONS = [
  { value: 15, label: "15 minutes" },
  { value: 30, label: "30 minutes" },
  { value: 60, label: "1 hour" },
  { value: 120, label: "2 hours" },
];

export default function ImpersonateModal({ tenant, onClose }) {
  const [reason, setReason] = useState("");
  const [ttl, setTtl] = useState(30);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (!reason.trim()) { setError("Reason is required for audit trail"); return; }
    setLoading(true);
    setError("");
    try {
      const res = await api.post(`/tenants/${tenant.tenant_id}/impersonate`, {
        reason: reason.trim(),
        ttl_minutes: ttl,
      });
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Impersonation failed");
    } finally {
      setLoading(false);
    }
  };

  const copyToken = () => {
    if (!result?.impersonation_token) return;
    navigator.clipboard.writeText(result.impersonation_token).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div
        className="glass-modal w-full max-w-md mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="impersonate-modal"
      >
        <div className="flex items-center justify-between p-5 border-b border-[#7C5CFF]/[0.08]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-amber-50 flex items-center justify-center">
              <UserCog className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <p className="font-bold text-[#0B0F1A] text-sm">Impersonate Tenant</p>
              <p className="text-[10px] text-[#5B617A]">{tenant.name}</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="imp-close" className="p-1.5 hover:bg-[#7C5CFF]/[0.06] rounded-lg">
            <X className="w-4 h-4 text-[#5B617A]" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {!result ? (
            <>
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-700">
                  This creates a <strong>read-only</strong> token scoped to this tenant. All actions are logged in the audit trail.
                </p>
              </div>

              {error && <p className="text-red-600 text-sm font-medium bg-red-50 p-2 rounded-xl" data-testid="imp-error">{error}</p>}

              <div>
                <label htmlFor="imp-reason" className="block text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider mb-1">
                  Reason (required)
                </label>
                <input
                  id="imp-reason"
                  data-testid="imp-reason-input"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full h-10 bg-white/60 border border-[#7C5CFF]/[0.12] rounded-xl px-3 text-sm text-[#0B0F1A] glass-input"
                  placeholder="e.g. Investigating billing issue"
                />
              </div>

              <div>
                <label className="block text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider mb-1">
                  Session duration
                </label>
                <div className="flex gap-2 flex-wrap">
                  {TTL_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      data-testid={`ttl-${opt.value}`}
                      onClick={() => setTtl(opt.value)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors
                        ${ttl === opt.value ? "bg-[#7C5CFF] text-white" : "bg-white/60 border border-[#7C5CFF]/[0.12] text-[#0B0F1A] hover:bg-[#7C5CFF]/[0.06]"}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <button
                data-testid="imp-submit"
                onClick={submit}
                disabled={loading}
                className="w-full h-10 rounded-xl text-white font-semibold text-sm flex items-center justify-center gap-2 btn-primary-glass"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserCog className="w-4 h-4" />Start impersonation</>}
              </button>
            </>
          ) : (
            <>
              <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                <div className="text-xs text-emerald-700">
                  <p className="font-semibold">Impersonation token created</p>
                  <p className="mt-0.5">Tenant: <strong>{result.target_tenant_name}</strong> · Owner: {result.target_owner_email}</p>
                  <p>Expires: {result.expires_at ? new Date(result.expires_at).toLocaleString() : "—"}</p>
                  <p className="mt-1 font-medium text-amber-700">{result.warning}</p>
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider mb-1">
                  Impersonation Token
                </label>
                <div className="relative">
                  <textarea
                    readOnly
                    value={result.impersonation_token}
                    data-testid="imp-token-output"
                    className="w-full bg-white/60 border border-[#7C5CFF]/[0.12] rounded-xl p-3 text-[10px] font-mono text-[#0B0F1A] h-20 resize-none"
                  />
                  <button
                    data-testid="imp-copy-btn"
                    onClick={copyToken}
                    className="absolute top-2 right-2 p-1.5 rounded-lg bg-white/80 border border-[#7C5CFF]/[0.12] hover:bg-[#7C5CFF]/[0.06] transition-colors"
                  >
                    {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5 text-[#5B617A]" />}
                  </button>
                </div>
              </div>

              <div className="bg-white/60 border border-[#7C5CFF]/[0.12] rounded-xl p-3">
                <p className="text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider mb-1">How to use</p>
                <ol className="text-xs text-[#0B0F1A] space-y-1 list-decimal list-inside">
                  <li>Copy the token above.</li>
                  <li>Open the tenant app in a new incognito window.</li>
                  <li>In DevTools Console, run: <code className="bg-[#7C5CFF]/[0.08] px-1 rounded text-[10px]">localStorage.setItem(&apos;aibm.jwt&apos;, &apos;TOKEN&apos;)</code></li>
                  <li>Refresh the page. You are now viewing as the tenant owner (read-only).</li>
                </ol>
              </div>

              <button
                data-testid="imp-done"
                onClick={onClose}
                className="w-full h-10 rounded-xl border border-[#7C5CFF]/[0.12] bg-white/60 text-[#0B0F1A] font-semibold text-sm hover:bg-[#7C5CFF]/[0.06] transition-colors"
              >
                Done
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
